import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import { LocationForm } from "./LocationForm";

interface Location {
 _id: string;
 country: string;
 countryCode: string;
 currency: string;
 currencySymbol: string;
 taxRate: number;
 taxIncluded: boolean;
 isActive: boolean;
 default?: boolean;
 mappedCountryCodes?: string[];
 createdAt: string;
}

export const Locations: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [locations, setLocations] = useState<Location[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showForm, setShowForm] = useState(false);
 const [editingId, setEditingId] = useState<string | null>(null);
 const [filterActive, setFilterActive] = useState<string>("");
 const [searchCountry, setSearchCountry] = useState("");

 useEffect(() => {
  fetchLocations();
 }, [filterActive]);

 const fetchLocations = async () => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams();
   if (filterActive) params.append("isActive", filterActive);
   if (searchCountry) params.append("countryCode", searchCountry.toUpperCase());
   
   const response = await api.get(`/locations/admin?${params.toString()}`);
   setLocations(response.data.locations || []);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.locations.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };

 const handleDelete = async (locationId: string) => {
  if (!window.confirm(t("admin.locations.confirmDelete"))) {
   return;
  }

  try {
   await api.delete(`/locations/admin/${locationId}`);
   fetchLocations();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.locations.errors.delete"));
  }
 };

 const handleEdit = (location: Location) => {
  setEditingId(location._id);
  setShowForm(true);
 };

 const handleFormClose = () => {
  setShowForm(false);
  setEditingId(null);
  fetchLocations();
 };

 if (loading && locations.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
   <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">{t("admin.locations.title")}</Heading>
     <div className="flex gap-2">
      <Button onClick={() => setShowForm(true)}>{t("admin.locations.addCountry")}</Button>
     </div>
    </div>

    {error && <Alert variant="error">{error}</Alert>}

    <LocationForm
     isOpen={showForm}
     locationId={editingId}
     onClose={handleFormClose}
     onSuccess={handleFormClose}
    />

    <Card padding="lg">
     <Stack spacing="md">
      <div className="flex gap-4 items-center">
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.locations.searchByCode")}
        </label>
        <input
         type="text"
         value={searchCountry}
         onChange={(e) => {
          setSearchCountry(e.target.value);
          if (e.target.value.length === 2) {
           fetchLocations();
          }
         }}
         placeholder={t("admin.locations.codePlaceholder")}
         maxLength={2}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
        />
       </div>
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.locations.filterByStatus")}
        </label>
        <select
         value={filterActive}
         onChange={(e) => setFilterActive(e.target.value)}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
         <option value="">{t("admin.locations.allStatus")}</option>
         <option value="true">{t("admin.active")}</option>
         <option value="false">{t("admin.inactive")}</option>
        </select>
       </div>
      </div>

      <Heading size="lg">{t("admin.locations.countries")}</Heading>
      {locations.length === 0 ? (
       <div className="text-center text-gray-500 py-8">
        {t("admin.locations.noCountries")}
       </div>
      ) : (
       <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
         <thead className="bg-gray-50">
          <tr>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.locations.country")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.locations.code")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.locations.currency")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.locations.taxRate")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.status")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Default
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Mapped Countries
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.actions")}
           </th>
          </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
          {locations.map((location) => (
           <tr key={location._id}>
            <td className="px-6 py-4 whitespace-nowrap">
             <button
              onClick={() => handleEdit(location)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              type="button"
             >
              {location.country}
             </button>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-blue-100 text-blue-800">
              {location.countryCode}
             </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             {location.currencySymbol} {location.currency}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             {location.taxRate}%
             {location.taxIncluded && (
              <span className="text-xs text-gray-400 ml-1">({t("admin.locations.included")})</span>
             )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <span
              className={`px-2 inline-flex text-xs leading-5 font-semibold ${
               location.isActive
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
              }`}
             >
              {location.isActive ? t("admin.active") : t("admin.inactive")}
             </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             {location.default ? (
              <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-purple-100 text-purple-800">
               Default
              </span>
             ) : (
              <span className="text-sm text-gray-400">—</span>
             )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             {location.mappedCountryCodes && location.mappedCountryCodes.length > 0 ? (
              <div className="flex flex-wrap gap-1">
               {location.mappedCountryCodes.map((code) => (
                <span
                 key={code}
                 className="px-2 inline-flex text-xs leading-5 font-semibold bg-blue-100 text-blue-800"
                >
                 {code}
                </span>
               ))}
              </div>
             ) : (
              <span className="text-gray-400">—</span>
             )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
             <div className="flex gap-2">
              <Button
               variant="outline"
               size="sm"
               onClick={() => handleEdit(location)}
              >
               {t("admin.edit")}
              </Button>
              <Button
               variant="outline"
               size="sm"
               onClick={() => handleDelete(location._id)}
               className="text-red-600 hover:text-red-700"
              >
               {t("admin.delete")}
              </Button>
             </div>
            </td>
           </tr>
          ))}
         </tbody>
        </table>
       </div>
      )}
     </Stack>
    </Card>
   </Stack>
  </Container>
 );
};

export default Locations;

